import React from 'react';
import { Box, Typography, Chip, IconButton, useTheme, Paper } from '@mui/material';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { useMessageContext } from '../../context/MessageContext';
import { useUI } from '../../context/UIContext';
import { useConfig } from '../../context/ConfigContext';
import ConditionalTooltip from '../common/ConditionalTooltip';

const ContextIndicatorContainer = ({ children }) => {
    const theme = useTheme();
    return (
        <Paper
            elevation={0}
            sx={{
                display: 'flex',
                alignItems: 'center',
                padding: theme.spacing(1.5, 2),
                borderRadius: theme.shape.borderRadius,
                backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(25, 118, 210, 0.15)'
                    : 'rgba(229, 246, 253, 0.85)',
                border: `1px solid ${theme.palette.mode === 'dark'
                    ? 'rgba(66, 133, 244, 0.5)'
                    : 'rgba(41, 182, 246, 0.5)'}`,
                flexShrink: 0,
                minWidth: 0,
            }}
        >
            {children}
        </Paper>
    );
};

const ContextIndicator = () => {
    const { getActiveContextDescription, hasActiveContext } = useMessageContext();
    const { isMobile } = useUI();
    const { showContextIndicator = true } = useConfig(); // Default to true if not set
    const theme = useTheme();

    // CONFIG CHECK: If disabled in appConfig, do not render anything.
    if (showContextIndicator === false) {
        return null;
    }

    const hasActiveContextValue = typeof hasActiveContext === 'function'
        ? hasActiveContext()
        : hasActiveContext;

    if (!hasActiveContextValue) return null;

    const contextDescriptions = getActiveContextDescription();
    if (!contextDescriptions || contextDescriptions.length === 0) return null;

    if (isMobile) {
        const firstDescription = contextDescriptions[0];
        return (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start' }}>
                <ConditionalTooltip title={`Context: ${firstDescription.text}`}>
                    <IconButton size="small" sx={{ border: 1, borderColor: 'divider' }}>
                        <InfoIcon color="primary" />
                    </IconButton>
                </ConditionalTooltip>
            </Box>
        );
    }

    return (
        <ContextIndicatorContainer>
            <InfoIcon fontSize="small" sx={{ mr: 1.5, color: theme.palette.mode === 'dark' ? '#90caf9' : '#0277bd', flexShrink: 0 }} />
            <Box sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                overflow: 'hidden'
            }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit', flexShrink: 0 }}>
                    Your messages include context from:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                    {contextDescriptions.map((desc, index) => (
                        <ConditionalTooltip key={`context-${index}`} title={desc.text}>
                            <Chip
                                icon={desc.icon}
                                label={desc.text}
                                size="small"
                                variant="outlined"
                                sx={{
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(41, 182, 246, 0.1)',
                                    color: theme.palette.mode === 'dark' ? '#ffffff' : '#01579b',
                                    borderColor: theme.palette.mode === 'dark' ? 'rgba(66, 133, 244, 0.5)' : 'rgba(41, 182, 246, 0.5)',
                                    fontWeight: 500,
                                    '& .MuiChip-icon': { color: 'inherit' },
                                    maxWidth: '250px',
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'inline-block',
                                    }
                                }}
                            />
                        </ConditionalTooltip>
                    ))}
                </Box>
            </Box>
        </ContextIndicatorContainer>
    );
};

export default React.memo(ContextIndicator);